/**
 * Google Ads helpers for Vehicle Listing Ads (VLA) remarketing and lead
 * conversions. Everything here is a no-op unless NEXT_PUBLIC_GOOGLE_AW_ID is
 * set, so unconfigured deployments keep their current behaviour. Events are
 * pushed with the standard gtag() argument shape, which both a bare gtag.js
 * install and the Google tag inside GTM understand.
 */
import { validAttemptId, type FlowEvent } from "./formFlow";
import { LEAD_VALUES, type LeadType } from "@/utils/ga4-events";

export const AD_CLICK_PARAMS = ["gclid", "gbraid", "wbraid", "gad_source", "msclkid", "fbclid", "ttclid", "oppref"] as const;

export function getGoogleAdsId(): string | undefined {
    const id = process.env["NEXT_PUBLIC_GOOGLE_AW_ID"]?.trim();
    return id ? id : undefined;
}

export function getGoogleAdsLeadLabel(): string | undefined {
    const label = process.env["NEXT_PUBLIC_GOOGLE_ACCEPTED_LEAD_LABEL"]?.trim();
    return label ? label : undefined;
}

/** Dedicated seller action: never fall back to the mixed retail action. */
export function getGoogleAdsSellerLeadLabel(): string | undefined {
    const label = process.env["NEXT_PUBLIC_GOOGLE_SELLER_LEAD_LABEL"]?.trim();
    return label ? label : undefined;
}

/** True when the landing URL carries a paid click id, so tags must not be deferred. */
export function hasAdClickId(search: string): boolean {
    if (!search) return false;
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    return AD_CLICK_PARAMS.some((key) => Boolean(params.get(key)));
}

type GtagWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };

function gtag(...args: unknown[]): void {
    if (typeof window === "undefined") return;
    const w = window as GtagWindow;
    if (typeof w.gtag === "function") {
        w.gtag(...args);
        return;
    }
    w.dataLayer = w.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
}

export interface AdsVehicleView {
    /** Merchant Center feed id: the stock number. */
    id: string;
    price?: number;
}

/**
 * Dynamic remarketing signal for VLA. `id` must match the Merchant Center
 * vehicle feed id (the stock number) and `google_business_vertical` must be "vehicle".
 */
export function trackAdsVehicleView(vehicle: AdsVehicleView): void {
    const awId = getGoogleAdsId();
    if (!awId || !vehicle.id) return;
    const payload: Record<string, unknown> = {
        send_to: awId,
        items: [{ id: vehicle.id, google_business_vertical: "vehicle" }]
    };
    if (typeof vehicle.price === "number" && vehicle.price > 0) payload["value"] = vehicle.price;
    gtag("event", "view_item", payload);
}

export interface AdsLeadConversion {
    leadType: Exclude<LeadType, "financing"> | "sell_my_car";
    /** Server-confirmed submission receipt, retained across retries. */
    transactionId: string | undefined;
}

const sentLeads = new Set<string>();

/** Called at the persistence receipt boundary, never from a thank-you page. */
export function trackAdsLeadConversion(lead: AdsLeadConversion): void {
    const awId = getGoogleAdsId();
    const label = lead.leadType === "sell_my_car" ? getGoogleAdsSellerLeadLabel() : getGoogleAdsLeadLabel();
    const receipt = validAttemptId(lead.transactionId);
    if (!awId || !label || !receipt || typeof window === "undefined") return;
    try {
        if (!/^(www\.)?thiscar\.com$/.test(window.location.hostname) ||
            window.localStorage.getItem("cookie-consent") !== "true") return;
    } catch { return; }
    const transactionId = `lead_${receipt}`;
    const key = `tc_google_${awId}_${label}_${transactionId}`;
    try {
        if (sentLeads.has(key) || window.sessionStorage.getItem(key)) return;
    } catch { /* Memory deduplication remains available. */ }
    try {
        gtag("event", "conversion", {
            send_to: `${awId}/${label}`,
            value: lead.leadType === "sell_my_car" ? 1 : LEAD_VALUES[lead.leadType],
            currency: "USD",
            transaction_id: transactionId
        });
        sentLeads.add(key);
        try { window.sessionStorage.setItem(key, "1"); } catch { /* Best effort. */ }
    } catch { /* Optional measurement cannot interrupt a saved lead. */ }
}

export function trackGoogleAdsFlowEvent(event: FlowEvent): void {
    const name = event["flow_name"];
    if (event.event !== "tc_flow_success" || event["flow_outcome"] !== "accepted") return;
    if (name !== "sell_my_car" && name !== "check_availability" && name !== "test_drive" && name !== "walkaround_video") return;
    trackAdsLeadConversion({ leadType: name, transactionId: String(event["flow_instance_id"]) });
}
